import type { Dispatch, SetStateAction } from 'react';
import {
	isP2PLikeSessionType,
	isP2PSessionType,
	isP2PTranscriptionOnlySessionType,
} from '@/utils/sessionType';

export {
	isP2PLikeSessionType,
	isP2PSessionType,
	isP2PTranscriptionOnlySessionType,
};

export const SESSION_CALL_PATH_SEGMENT = '/sessao-chamada';

/** false → layout Plumaa custom (SessionCallPageShell.custom.tsx). */
export const USE_DAILY_PREBUILT_IFRAME = true;

export type SessionCallViewerRole = 'user' | 'professional';

export type SessionCallCompanyBanner = {
	url?: string | null;
	name?: string | null;
};

export type VideoCallHeaderConfig = {
	onBack: () => void;
	onLogoClick?: () => void;
	onToggleLayout: () => void;
	activeSpeakerMode: boolean;
	controlsEnabled: boolean;
	showLayoutToggle?: boolean;
	/** Oculta o botão voltar do header (ex.: transição pós-sessão). */
	hideBackButton?: boolean;
	/** Horário marcado da sessão (ms). Timer do profissional no header. */
	meetingScheduledStartMs?: number | null;
	/** Oculta o timer da reunião no header (ex.: transcrição colapsada). */
	hideMeetingTimer?: boolean;
};

export type SessionCallLayoutOutletContext = {
	setVideoCallHeaderConfig: Dispatch<
		SetStateAction<VideoCallHeaderConfig | null>
	>;
};

export function isSessionCallRoute(pathname: string): boolean {
	return /(^|\/)sessao-chamada(\/|$)/.test(pathname);
}

/** `location.state` ao navegar por entradas internas da chamada. */
export type SessionCallAccessLocationState = {
	fromSessionAccess: true;
	viewerRole: SessionCallViewerRole;
	companyBanner?: SessionCallCompanyBanner;
	sessionStatus?: Sessions.SessionStatus;
	sessionType?: Sessions.SessionType;
	isOnline?: boolean;
	transcriptionAllowed?: boolean;
	transcriptionMock?: boolean;
	credentials?: SessionCall.Credentials;
};

export function createSessionCallAccessState(
	viewerRole: SessionCallViewerRole,
	companyBanner?: SessionCallCompanyBanner,
	sessionStatus?: Sessions.SessionStatus,
	credentials?: SessionCall.Credentials,
	sessionType?: Sessions.SessionType,
	options?: {
		isOnline?: boolean;
		transcriptionAllowed?: boolean;
		transcriptionMock?: boolean;
	},
): SessionCallAccessLocationState {
	return {
		fromSessionAccess: true,
		viewerRole,
		...(companyBanner ? { companyBanner } : {}),
		...(sessionStatus ? { sessionStatus } : {}),
		...(sessionType ? { sessionType } : {}),
		...(options?.isOnline !== undefined
			? { isOnline: options.isOnline }
			: {}),
		...(options?.transcriptionAllowed !== undefined
			? { transcriptionAllowed: options.transcriptionAllowed }
			: {}),
		...(options?.transcriptionMock !== undefined
			? { transcriptionMock: options.transcriptionMock }
			: {}),
		...(credentials ? { credentials } : {}),
	};
}

export function isSessionEligibleForCallProntuario(options: {
	sessionType?: Sessions.SessionType | null;
	isOnline?: boolean;
	transcriptionAllowed?: boolean;
	transcriptionMock?: boolean;
}): boolean {
	if (isP2PTranscriptionOnlySessionType(options.sessionType)) return true;
	if (isP2PSessionType(options.sessionType)) return true;
	const transcriptionEnabled =
		options.transcriptionAllowed === true ||
		options.transcriptionMock === true;
	return (
		options.sessionType === 'DEFAULT' &&
		options.isOnline === true &&
		transcriptionEnabled
	);
}

export function shouldShowProfessionalEndSession(options: {
	viewerRole: SessionCallViewerRole;
	sessionStatus?: Sessions.SessionStatus | null;
	professionalSessionEnded?: boolean;
}): boolean {
	return (
		options.viewerRole === 'professional' &&
		options.sessionStatus === 'STARTED' &&
		!options.professionalSessionEnded
	);
}

export function shouldShowSessionCallProntuario(
	viewerRole: SessionCallViewerRole,
	options?: {
		hasError?: boolean;
		sessionType?: Sessions.SessionType | null;
		isOnline?: boolean;
		transcriptionAllowed?: boolean;
		transcriptionMock?: boolean;
		/** Profissional entrou na sala Daily (joined-meeting) ou sessão P2P transcrição ativa. */
		hasJoinedCall?: boolean;
	},
): boolean {
	if (options?.hasError) return false;
	if (viewerRole !== 'professional') return false;
	if (
		!isSessionEligibleForCallProntuario({
			sessionType: options?.sessionType,
			isOnline: options?.isOnline,
			transcriptionAllowed: options?.transcriptionAllowed,
			transcriptionMock: options?.transcriptionMock,
		})
	) {
		return false;
	}
	if (isP2PTranscriptionOnlySessionType(options?.sessionType)) {
		return options?.hasJoinedCall === true;
	}
	if (options?.hasJoinedCall !== true) return false;
	return true;
}

export function isSessionTranscriptionMock(
	session?: Pick<Sessions.Session, 'transcription_mock'> | null,
): boolean {
	return session?.transcription_mock === true;
}

export function isSessionTranscriptionAllowed(
	session?: Pick<
		Sessions.Session,
		'transcription_allowed' | 'transcription_mock'
	> | null,
): boolean {
	return (
		session?.transcription_allowed === true ||
		session?.transcription_mock === true
	);
}

export const PRONTUARIO_TRANSCRIPTION_BADGE_HINT =
	'Esta sessão está sendo registrada e transcrita. Clique aqui para abrir o prontuário.';

export const PRONTUARIO_NO_TRANSCRIPTION_BADGE_HINT =
	'Esta sessão não será transcrita. Clique para abrir o prontuário e registrar anotações.';

export const PRONTUARIO_TRANSCRIPTION_HEADER_HINT =
	'Toque na gravação para abrir a transcrição em tempo real.';

export const PRONTUARIO_NO_TRANSCRIPTION_HEADER_HINT =
	'Esta sessão não será transcrita. Use o prontuário para anotações manuais.';

/** Exibida no chat ao parar a captura ao vivo no prontuário (não afeta a transcrição oficial). */
export const PRONTUARIO_LIVE_CAPTURE_STOPPED_NOTICE =
	'Texto ao vivo no prontuário interrompido. A gravação e a transcrição oficial da consulta continuam normalmente e serão salvas ao final da sessão.';

export function getSessionCallCompanyBanner(
	session: Sessions.Session,
): SessionCallCompanyBanner | undefined {
	const primaryParticipantUser = (
		session as {
			participants?: Array<{
				user?: {
					employee?: {
						company?: unknown;
					} | null;
				} | null;
			}>;
		}
	).participants?.[0]?.user;

	const company = (session.user?.employee?.company ??
		primaryParticipantUser?.employee?.company) as
		| {
				name?: string | null;
				trade_name?: string | null;
				legal_name?: string | null;
				banner_img_url?: string | null;
				logo_img_url?: string | null;
		  }
		| null
		| undefined;

	if (!company) {
		return undefined;
	}

	const name =
		company.trade_name?.trim() ||
		company.legal_name?.trim() ||
		company.name?.trim() ||
		null;
	const url = company.banner_img_url ?? company.logo_img_url ?? null;

	if (!name && !url) {
		return undefined;
	}

	return { url, name };
}

export function isSessionCallAccessState(
	state: unknown,
): state is SessionCallAccessLocationState {
	return (
		typeof state === 'object' &&
		state !== null &&
		(state as SessionCallAccessLocationState).fromSessionAccess === true &&
		((state as SessionCallAccessLocationState).viewerRole === 'user' ||
			(state as SessionCallAccessLocationState).viewerRole ===
				'professional')
	);
}
